// src/components/MarketResearch/ReportHeader.tsx
import React from 'react';
import {
  Box,
  Typography,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  TrendingUpOutlined,
  LocationOnOutlined,
  CalendarTodayOutlined,
  AssessmentOutlined
} from '@mui/icons-material';
import { ExtendedMarketResearch } from '../types';

interface ReportHeaderProps {
  title: string;
  subtitle: string;
  marketResearch: ExtendedMarketResearch[];
}

export const ReportHeader: React.FC<ReportHeaderProps> = ({
  title,
  subtitle,
  marketResearch
}) => {
  const theme = useTheme();

  const getReportDate = () => {
    if (marketResearch.length > 0 && marketResearch[0].createdAt) {
      return new Date(marketResearch[0].createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getLocation = () => {
    if (marketResearch.length > 0) {
      // Try to get location from research data, fallback to simple market location
      const data = marketResearch[0].research_data;
      if (data) {
        // Check if there's any location info we can extract
        return 'Market Location'; // Simplified for now
      }
    }
    return 'Market Location';
  };

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        borderRadius: 2,
        p: 4,
        mb: 3,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
        '@media print': {
          background: 'none !important',
          color: '#000 !important',
          boxShadow: 'none !important',
          border: `2px solid ${theme.palette.primary.main}`,
        },
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          background: `radial-gradient(circle at top right, ${alpha('#ffffff', 0.1)} 0%, transparent 70%)`,
          '@media print': { display: 'none' },
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 3,
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 0 }
          }}
        >
          {/* Title Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <AssessmentOutlined sx={{ fontSize: 32 }} />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  '@media print': { color: '#000 !important' }
                }}
              >
                {title}
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                fontWeight: 400,
                '@media print': { color: '#666 !important' }
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Status Chip */}
          <Chip
            icon={<TrendingUpOutlined />}
            label="Latest Analysis"
            variant="filled"
            sx={{
              backgroundColor: alpha('#ffffff', 0.2),
              color: 'white',
              fontWeight: 600,
              '& .MuiChip-icon': {
                color: 'white',
              },
              '@media print': {
                backgroundColor: 'transparent !important',
                color: '#000 !important',
                border: '1px solid #000 !important',
                '& .MuiChip-icon': {
                  color: '#000 !important',
                },
              },
            }}
          />
        </Box>

        {/* Metadata Row */}
        <Box
          sx={{
            display: 'flex',
            gap: 4,
            flexWrap: 'wrap',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnOutlined sx={{ fontSize: 18, opacity: 0.8 }} />
            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                '@media print': { color: '#666 !important' }
              }}
            >
              {getLocation()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayOutlined sx={{ fontSize: 18, opacity: 0.8 }} />
            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                '@media print': { color: '#666 !important' }
              }}
            >
              Generated: {getReportDate()}
            </Typography>
          </Box>

          {marketResearch.length > 0 && (
            <Chip
              label={`${marketResearch.length} Report${marketResearch.length > 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
              sx={{
                color: 'white',
                borderColor: alpha('#ffffff', 0.5),
                '@media print': {
                  color: '#000 !important',
                  borderColor: '#000 !important',
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
